/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings } from '@styles/index';
import H2 from '@base/H2/H2';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import P1 from '@base/P1/P1';
import Link from 'next/link';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const StyledOrderedList = styled.ol`
  padding-left: ${Spacings.SPACING_4B};
`;

const Terms = () => {
  const { pathname } = useRouter();
  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>
      <RightContainer>
        <H2>Unacademy Notes Terms and Conditions</H2>
        <br />
        <br />
        <br />
        <P1>
          Unacademy Notes <i>(defined below)</i> is one of the Services{' '}
          <i>
            (as defined in the <Link href="/terms">Terms and Conditions</Link>)
          </i>{' '}
          offered to you by Unacademy. <b>“Unacademy Notes”</b> comprises of (i)
          physical study material in the form of physical / hard-copy books,
          booklets, notes, etc., that is either bundled with the services
          offered to Users or Subscriptions{' '}
          <i>
            (as defined in the{' '}
            <Link href="/terms#terms">Terms and Conditions</Link>)
          </i>{' '}
          purchased by them, or is offered to Users for a standalone purchase{' '}
          <b>(“Physical Study Material”)</b> and (ii) digital study material in
          the form of the books, notes, and other material in digital format
          that is either bundled with the services offered to Users or
          Subscriptions{' '}
          <i>
            (as defined in the{' '}
            <Link href="/terms#terms">Terms and Conditions</Link>)
          </i>{' '}
          purchased by them, or is offered to Users for a standalone purchase{' '}
          <b>(“Digital Study Material”)</b>. These Unacademy Notes Terms and
          Conditions <b>(“Notes Terms”)</b> governs your use of and access to
          the Unacademy Notes purchased or subscribed to by you (as applicable)
          on the Unacademy Platform{' '}
          <i>
            (as defined in the <Link href="/terms">Terms and Conditions</Link>)
          </i>
          .
        </P1>
        <br />
        <P1>
          When we refer to <b>“you”</b> or <b>“your”</b> or <b>“yourself”</b> or{' '}
          <b>“User”</b>, we mean all users of the Platform who access and/or use
          the Unacademy Notes or any part thereof. Similarly, when we speak of{' '}
          <b>“Unacademy”</b>, <b>“we”</b>, <b>“us”</b>, and/or <b>“our”</b>, we
          collectively mean <b>Sorting Hat Technologies Private Limited</b> — a
          company incorporated in India and all of its affiliates which have
          licence to host the Platform and offer Services. Kindly refer to the{' '}
          <b>‘About Unacademy and the Platform’</b> section of the{' '}
          <Link href="/terms#about">Terms and Conditions</Link> to know more
          about the entity.
        </P1>
        <br />
        <P1>
          These Notes Terms are an electronic record under the Information
          Technology Act, 2000 read with rules and regulations made thereunder
          and are generated by a computer system; and will not require any
          physical, electronic, or digital signature. These Notes Terms along
          with the Platform Terms <i>(defined below)</i> collectively form a
          legally binding agreement between you and Unacademy in respect of your
          use of the Unacademy Notes. The{' '}
          <Link href="/terms">Terms and Conditions</Link>,{' '}
          <Link href="/privacy">Privacy Policy</Link>,{' '}
          <Link href="/cookie-policy">Cookies Policy</Link>,{' '}
          <Link href="/guidelines">User Guidelines</Link> and any other terms
          and conditions, rules, policies or guidelines included in these Notes
          Terms, Terms and Conditions or Privacy Policy by reference, and/or as
          updated on the Platform or otherwise communicated to you from time to
          time (including terms and conditions of other third party platforms
          that may be used or accessed for gaining access to the Platform or
          using the Services on the Platform) shall collectively be referred to
          as the <b>“Platform Terms”</b>.
        </P1>
        <br />
        <P1>
          All capitalized terms used and not defined in these Notes Terms shall
          have the meaning ascribed to them under the Platform Terms.
        </P1>
        <br />
        <P1>
          These Notes Terms are split into three parts: <b>(i) Part – I:</b>{' '}
          Terms applicable for use of Physical Study Material(s);{' '}
          <b>(ii) Part — II:</b> Terms applicable for use of Digital Study
          Material; and <b>(iii) Part – III:</b> General Terms and Conditions
          applicable for Unacademy Notes. Please read these Notes Terms
          carefully before purchasing or otherwise subscribing to Unacademy
          Notes or any part thereof on the Unacademy Platform. If you do not
          agree to any of the terms or do not wish to be bound by them, then
          please do not use the Platform or any Services offered therein in any
          manner.
        </P1>
        <br />
        <P1>
          Unacademy retains an unconditional right to modify or amend these
          Notes Terms at any time. You can determine when these Notes Terms were
          last modified by referring to the “Last Updated Date” specified at the
          end of this document. It shall be your responsibility to check these
          Notes Terms periodically for changes. Your acceptance of the amended
          Notes Terms shall signify your consent to such changes and agreement
          to be legally bound by the same.
        </P1>
        <br />
        <StyledOrderedList type="I">
          <li>
            <P1>
              <b>PART — I: Physical Study Material(s) Terms and Conditions:</b>
            </P1>
            <br />
            <P1>
              These Physical Study Material Terms and Conditions are applicable
              for your purchase of Physical Study Material(s) on the Platform.
              The Physical Study Material(s) are made available to you in two
              ways: (i) Physical Study Material(s) is bundled with certain
              Subscriptions offered for purchase to Users or with other services{' '}
              <b>(“Bundled Physical Notes”)</b>, and (ii) Physical Study
              Material is made available for purchase on a standalone basis
              i.e., Users can purchase it independent of any Subscriptions on
              the Platform <b>(“Unbundled Physical Notes”)</b>. The terms
              applicable for purchase of Bundled and Unbundled Physical Notes
              vary and are specified as such hereinbelow.
            </P1>
            <br />
            <StyledOrderedList type="A">
              <li>
                <P1>
                  <b>General terms applicable to Physical Study Materials:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>End-Use:</b> Your purchase of the Physical Study
                      Material(s) entitles you to use the same for your personal
                      use only. Having possession of the Physical Study Material
                      pursuant to your purchase thereof, whether as part of
                      bundled or unbundled service, does not entitle you, either
                      by yourself or through any third-party, to sell,
                      distribute, reproduce, copy, publish, transmit, broadcast,
                      display, licence, or otherwise exploit the Physical Study
                      Materials or any part thereof, in any manner whatsoever.
                      In other words, the rights granted to you in respect of
                      the Physical Study Materials purchased by you, whether as
                      part of bundled or unbundled service, only allows you to
                      use it for your personal use, and you cannot use it for
                      any other purpose, including without limitation,
                      distributing physical and/or digital copies of the same
                      for non-commercial purposes. To understand the rights
                      granted to you, please refer to the{' '}
                      <b>
                        ‘Intellectual Property in Physical Study Material(s)’
                      </b>{' '}
                      section below.
                    </P1>
                    <br />
                    <P1>
                      Further, the rights granted to you is limited to using the
                      Physical Study Material on an ‘as-is’ basis, and it does
                      not entitle you to modify, change or tamper with the
                      contents of the Physical Study Materials, Unacademy’s
                      branding on the Physical Study Materials or do any act or
                      omission that infringes the rights{' '}
                      <i>
                        (including but not limited to the intellectual property
                        rights)
                      </i>{' '}
                      of Unacademy or any other third party in relation to the
                      Physical Study Materials.
                    </P1>
                    <br />
                    <P1>
                      Unacademy shall have the right to take necessary legal
                      action against you, if it comes to Unacademy’s notice that
                      the Physical Study Material(s) or any part thereof that is
                      subscribed or purchased by you, , is used in an
                      unauthorized manner.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      <b>
                        Intellectual Property in Physical Study Material(s):
                      </b>{' '}
                      Unacademy sells the Physical Study Materials to you on an
                      “as is” basis and Unacademy neither owns any right
                      (including any intellectual property rights therein),
                      title or interest in the Physical Study Materials, nor
                      does it create, change, or modify, the content of the
                      Physical Study Materials. You acknowledge that Unacademy
                      does not transfer to you the intellectual property rights
                      in the content of the Physical Study Materials.
                    </P1>
                    <br />
                    <P1>
                      Subject to completion of your purchase as stated in these
                      Notes Terms, ownership and title of the Physical Study
                      Materials purchased by you, either as part of bundled or
                      unbundled service, shall pass to you from Unacademy upon
                      the delivery of the Physical Study Materials to you.
                      Notwithstanding the foregoing, the ownership granted to
                      you is limited for your personal use of the Physical Study
                      Material purchased by you, and such grant of rights does
                      not entitle you to exploit or otherwise use the Physical
                      Study Material, including without limitation, sharing
                      excerpts of the same through WhatsApp and/or other
                      communication and social media platforms for
                      non-commercial purposes. Please refer to the{' '}
                      <b>‘End Use’</b> section above to understand the nature
                      and extent to which you are entitled to use the Physical
                      Study Materials.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      <b>Delivery and Shipping of Physical Study Materials:</b>{' '}
                      To facilitate delivery of the Physical Study Material
                      purchased by you, you are required to provide us with your
                      shipping address and pin code along with other contact
                      information (including your phone number) as may be
                      requested on the Platform at the time of your purchase. In
                      the event your Order{' '}
                      <i>(as defined under Part - III of these Notes Terms)</i>{' '}
                      is not serviceable by us or our delivery partners, or the
                      area is not covered, we would request you to provide us
                      with an alternate shipping address which we expect to have
                      on our partner’s delivery list.
                    </P1>
                    <br />
                    <P1>
                      If you order multiple Physical Study Materials in one
                      transaction, we will endeavour to ship all of them
                      together. However, the same may not be possible due to
                      some product characteristics and / or logistics’ issues.
                      If you wish to ship the Physical Study Materials to
                      multiple addresses, you must book the Orders separately
                      with different shipping addresses. We shall not be liable
                      for any loss or damage caused to you due to you providing
                      incorrect shipping address or contact details.
                    </P1>
                    <br />
                    <P1>
                      While we shall endeavour to deliver the Physical Study
                      Materials to you in the earliest possible time frame
                      through our delivery partners, we will not be liable for
                      any loss or damage caused to you due to any delay caused
                      by our delivery partners in the delivery of the Physical
                      Study Materials to you.
                    </P1>
                    <br />
                    <P1>
                      Further, please note that purchase of Physical Study
                      Materials does not entitle you to receive digital copies
                      or versions of such Physical Study Materials.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      <b>
                        Cancellations and Returns of Bundled Physical Notes:
                      </b>{' '}
                      Once you have completed the payment for an Order placed by
                      you, you will not be able to cancel the Order, either as a
                      whole or in part (i.e., cancel the services offered as
                      part of the bundle individually).
                    </P1>
                    <br />
                    <P1>
                      But, if you receive a damaged or a defective unit of the
                      Physical Study Material, then you may request for a
                      replacement by writing to us at{' '}
                      <Link href="/helpcenter">Unacademy Learner Support</Link>{' '}
                      within 20 (twenty) days from the date your Order has been
                      delivered to you. We shall evaluate the veracity of your
                      claims and process a replacement of such a unit in
                      accordance with the applicable laws. Subject to the
                      applicable laws, our decision on whether your claim is
                      genuine shall be final and you agree not to dispute the
                      same in any manner whatsoever. Further, you agree that
                      until the damaged/defective unit of the Physical Study
                      Material is provided back to us in the condition it was
                      delivered to you, we will not process your replacement.
                      Notwithstanding the foregoing, we will not be liable for
                      any loss or damage caused to you due to receipt of damaged
                      or defective unit of Physical Study Material or due to any
                      delay caused to you in receiving the replacement.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
              <li>
                <P1>
                  <b>Terms applicable to Bundled Physical Notes:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>Purchase of Bundled Physical Notes:</b> Upon purchase
                      of a Bundled Physical Notes, you may be entitled to
                      receive copy(ies) of the relevant Physical Study
                      Materials, depending upon the nature of the bundled
                      service. The cost for receiving the copy of such Physical
                      Study Materials shall be included as part of the Price you
                      are required to pay for the Bundled Physical Notes, and
                      you shall not be required to purchase the same separately
                      or pay any additional cost. Notwithstanding the foregoing,
                      Unacademy may, at its sole discretion, provide Physical
                      Study Material(s) on a complimentary basis to any User,
                      upon their purchase of a Subscription or other service on
                      the Platform, and/or offer the same as a ‘prize’ or for
                      availing other Services or upon winning any contest on the
                      Unacademy Platform. It is hereby clarified that, offering
                      Physical Study Materials to certain User(s) on a
                      complimentary basis or as part of Unacademy’s Services,
                      does not automatically entitle you to receive such
                      Physical Study Materials free of cost.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
              <li>
                <P1>
                  <b>Terms applicable to Unbundled Physical Study Materials:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>Purchase of Unbundled Physical Notes:</b> You may
                      purchase one or more Physical Study Material(s)
                      independently, i.e., not as part of any Subscriptions or
                      other services offered on the Platform. You can place an
                      Order for purchase of Physical Study Material(s) by
                      following the instructions provided in the relevant
                      section of the Platform and making payment of the Price{' '}
                      <i>(defined below)</i>. If you are facing any issues while
                      placing the Order on the Platform, then please write to us
                      at{' '}
                      <Link href="/helpcenter">Unacademy Learner Support</Link>.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
            </StyledOrderedList>
          </li>
          <li>
            <P1>
              <b>PART — II: Digital Study Material(s) Terms and Conditions</b>
            </P1>
            <br />
            <P1>
              These Digital Study Material Terms and Conditions are applicable
              for your purchase of Digital Study Material(s) on the Platform.
              The Digital Study Material(s) are made available to you in two
              ways: (i) Digital Study Material(s) is bundled with certain
              Subscriptions offered for purchase to Users or with other services{' '}
              <b>(“Bundled Digital Notes”)</b>, and (ii) Digital Study Material
              is made available for purchase on a standalone basis i.e., Users
              can purchase it independent of any Subscriptions on the Platform{' '}
              <b>(“Unbundled Digital Notes”)</b>. The terms applicable for
              purchase of Bundled and Unbundled Digital Study Materials vary and
              are specified as such hereinbelow.
            </P1>
            <br />
            <StyledOrderedList type="A">
              <li>
                <P1>
                  <b>General terms applicable to Digital Study Materials:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>End-Use:</b> Your purchase of the Digital Study
                      Material(s) entitles you to use the same for your personal
                      use. However, the rights in and to the Digital Study
                      Material that is granted to you pursuant to your purchase
                      thereof, is only meant for your personal use and does not
                      allow you, either by yourself or through any third-party,
                      to sell, distribute, reproduce, copy, publish, transmit,
                      broadcast, display, licence, or otherwise exploit the
                      Digital Study Materials or any part thereof in any manner
                      whatsoever. In other words, the rights granted to you in
                      respect of the Digital Study Materials purchased by you
                      only allows you to use it for your personal use, and you
                      cannot use it for any other purpose, including without
                      limitation, sharing it with others for non-commercial
                      purposes. To understand the rights granted to you, please
                      refer to the <b>’Licence to Digital Study Material’</b>{' '}
                      and/or <b>‘Purchase of Unbundled Digital Notes’</b>{' '}
                      sections below depending on the type of Digital Study
                      Material subscribed and/or purchased by you.
                    </P1>
                    <br />
                    <P1>
                      Further, the rights granted to you is limited to using the
                      Digital Study Material on an ‘as-is’ basis, and it does
                      not entitle you to modify, change or tamper with the
                      contents of the Digital Study Materials, Unacademy’s
                      branding on the Digital Study Materials or do any act or
                      omission that infringes the rights{' '}
                      <i>
                        (including but not limited to the intellectual property
                        rights)
                      </i>{' '}
                      of Unacademy or any other third party in relation to the
                      Digital Study Materials.
                    </P1>
                    <br />
                    <P1>
                      Unacademy shall have the right to take necessary legal
                      action against you, if it comes to Unacademy’s notice that
                      the Digital Study Material(s) or any part thereof that is
                      subscribed or purchased by you is used in an unauthorized
                      manner.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      <b>Delivery of Digital Study Materials:</b> Upon
                      completion of your purchase of Bundled or Unbundled
                      Digital Notes or other relevant service offered on the
                      Platform, you shall be entitled to access and use the
                      Digital Study Material(s) subject to limitations
                      prescribed under these Notes Terms. You may read the
                      Digital Study Material within the Platform and/or download
                      it to your personal device(s) (subject to its
                      compatibility), as applicable, by following the
                      instructions provided on the designated page of the
                      Platform.
                    </P1>
                    <br />
                    <P1>
                      The Digital Study Material(s) may be made available to you
                      in Portable Document Format (PDF) or in any other format
                      as may be deemed fit by Unacademy. You shall not be
                      entitled to make any special requests regarding the form
                      and/or format in which Digital Study Materials are
                      delivered to you.
                    </P1>
                    <br />
                    <P1>
                      You agree that you alone shall be responsible to ensure
                      that Digital Study Materials made available to you are
                      compatible/readable/usable/accessible to you on your
                      device. Unacademy does not and will not assume any
                      responsibility in this regard.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      <b>Intellectual Property:</b> Unacademy sells the Digital
                      Study Materials to you on an “as is” basis and Unacademy
                      neither owns any right (including any intellectual
                      property rights therein), title or interest in the Digital
                      Study Materials, nor does it create, change, or modify,
                      the content of the Digital Study Materials. You
                      acknowledge that Unacademy does not transfer to you the
                      intellectual property rights in the content of the Digital
                      Study Materials.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
              <li>
                <P1>
                  <b>Terms applicable for use of Bundled Digital Notes:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>Licence to Digital Study Material:</b> Upon purchase of
                      a Bundled Digital Notes, you may be provided access to the
                      relevant Digital Study Materials, depending upon the
                      nature of the bundled service. The cost for accessing such
                      Digital Study Materials shall be included as part of the
                      Price you are required to pay for the Bundled Digital
                      Notes, and you shall not be required to purchase the same
                      separately or pay any additional cost. Notwithstanding the
                      foregoing, Unacademy may, at its sole discretion, provide
                      complimentary access to Digital Study Material(s) to any
                      User, upon their purchase of a Subscription or other
                      service on the Platform, and/or offer such access as a
                      ‘prize’ or for availing other Services or upon winning any
                      contest on the Unacademy Platform. It is hereby clarified
                      that, offering Digital Study Materials (including the ones
                      purchased by you) to certain User(s) on a complimentary
                      basis or as part of Unacademy’s Services, does not
                      automatically entitle to receive such Digital Study
                      Materials free of cost.
                    </P1>
                    <br />
                    <P1>
                      In the aforesaid instances or any other instance where
                      access to Digital Study Materials is provided to you as a
                      part of the Service(s) availed by you, Unacademy provides
                      you a limited, non-exclusive, revocable, non-sublicensable
                      end-user licence to access and use the Digital Study
                      Materials for personal consumption only. It is hereby
                      clarified that, where access to Digital Study Material is
                      provided to you as part of any Service availed by you on
                      the Platform, the ownership and intellectual property
                      rights in such Digital Study Materials does not pass to
                      you from Unacademy. Please refer to the <b>‘End Use’</b>{' '}
                      section above to understand the nature and extent to which
                      you are entitled to use the Digital Study Materials.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
              <li>
                <P1>
                  <b>Terms applicable for use of Unbundled Digital Notes:</b>
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      <b>Purchase of Unbundled Digital Notes:</b> You may
                      purchase one or more Digital Study Material(s)
                      independently, i.e., not as part of any Subscriptions or
                      other services offered on the Platform. You can place an
                      Order for purchase of Digital Study Material(s) by
                      following the instructions provided in the relevant
                      section of the Platform and making payment of the Price{' '}
                      <i>(defined below)</i>. If you are facing any issues while
                      placing the Order on the Platform, then please write to us
                      at{' '}
                      <Link href="/helpcenter">Unacademy Learner Support</Link>.
                    </P1>
                    <br />
                    <P1>
                      Upon completion of your purchase of the Digital Study
                      Material(s), the ownership and title of such Digital Study
                      Material(s), on ‘as is’ basis, shall pass to you from
                      Unacademy. However, the ownership granted to you is
                      limited to your personal use of the Digital Study Material
                      purchased by you, and such grant of rights does not
                      entitle you to exploit or otherwise use the Digital Study
                      Material, including without limitation, sharing excerpts
                      of the same through WhatsApp and/or other communication
                      and social media platforms for non-commercial purposes.
                      Please refer to the <b>‘End Use’</b> section above to
                      understand the nature and extent to which you are entitled
                      to use the Digital Study Materials.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
            </StyledOrderedList>
          </li>
          <li>
            <P1>
              <b>
                PART – III: General Terms and Conditions Applicable to Unacademy
                Notes
              </b>
            </P1>
            <br />
            <P1>
              These General Terms and Conditions are applicable to your use of
              the Unacademy Notes.
            </P1>
            <br />
            <StyledOrderedList type="A">
              <li>
                <P1>
                  <b>Pricing and Payment:</b> To purchase the Unacademy Notes,
                  you are required to place an order following the instruction
                  provided in the relevant section of the Platform{' '}
                  <b>(“Order”)</b> and pay the price/fee displayed therein{' '}
                  <b>(“Price”)</b>. You agree and acknowledge that the access to
                  the Unacademy Notes as contemplated in these Notes Terms and
                  the option to download the Digital Study Material, as
                  applicable, shall be subject to you making full payment of the
                  applicable Price to Unacademy.
                </P1>
                <br />
                <P1>
                  You understand and agree that the Prices charged and displayed
                  on the Platform for the Unacademy Notes may be subject to
                  change without any notice or consequential liability to you.
                  However, you shall only be required to pay the Price displayed
                  to you at the time of completing your purchase. Further, if
                  you have purchased a Bundled Digital or Physical Notes,
                  revised Prices, if any, for such offerings shall be made
                  applicable to you only from the next billing period or
                  renewal, if you choose to re-purchase/renew the offering.
                </P1>
                <br />
                <P1>
                  You shall not have any right to negotiate the Price.
                  Unacademy, may at its sole discretion provide offers/discounts
                  on the Price, such offers/discounts shall be communicated to
                  you directly on the Unacademy Platform or via email, SMS,
                  phone, or such other means of communication, and can be
                  availed only through the Unacademy Platform unless otherwise
                  specifically communicated to you by Unacademy.
                </P1>
                <br />
                <P1>
                  Further, we reserve the right to offer custom Prices to you
                  (including discounts and / or special offers) in addition to
                  what is offered on the Unacademy Platform, which include
                  offering custom billing and payment terms, that are different
                  from our standard terms.
                </P1>
                <br />
                <P1>
                  The following terms shall be applicable to your payments on
                  the Unacademy Platform –
                </P1>
                <br />
                <StyledOrderedList type="1">
                  <li>
                    <P1>
                      Your payments to Unacademy shall be subject to applicable
                      taxes including without limitation Goods and Service Taxes
                      (GST) and Value Added Taxes (VAT) or other similar taxes
                      as may be applicable in your country of residence/from
                      where you have created your account on the Platform/ made
                      the purchase from.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      As stated in the{' '}
                      <Link href="/terms#pricing">
                        ‘Pricing, Payments, and Refunds’
                      </Link>{' '}
                      section of our Terms and Conditions, we use Third-Party
                      Service Providers{' '}
                      <i>
                        (as defined in the said section of our Terms and
                        Conditions)
                      </i>{' '}
                      to process payments for the sale of Subscriptions,
                      Unacademy Notes and other services on the Platform.
                      Accordingly, to the extent applicable and relevant to your
                      purchase of the Unacademy Notes on the Platform, all terms
                      stated in the abovementioned section of the Terms and
                      Conditions shall continue to apply. Hence, please read the
                      terms carefully before you make any payments to us.
                      Notwithstanding the foregoing, if there is any conflict
                      between the terms stated in the aforesaid section of our
                      Terms and Conditions and these Notes Terms, then to the
                      extent of such conflict these Notes Terms shall prevail
                      with respect to Unacademy Notes.
                    </P1>
                    <br />
                  </li>
                  <li>
                    <P1>
                      To complete your purchase, you may be redirected to an
                      external website operated by the Third-Party Service
                      Provider. We cannot and do not (i) guarantee the adequacy
                      of the privacy and security practices employed by or the
                      content and media provided by the Third-Party Service
                      Provider or its respective websites or (ii) control
                      collection or use of your personal information by such
                      Third-Party Service Provider. Hence, prior to using any
                      services offered by a Third-Party Service Provider, we
                      suggest that you read their terms and conditions, privacy
                      policy and other policies, that may apply, to understand
                      their terms of usage and to understand how your personal
                      information is being processed.
                    </P1>
                    <br />
                    <P1>
                      Unacademy is not affiliated to any Third-Party Service
                      Provider and neither Unacademy nor any of the Third-Party
                      Service Provider are agents or employees of the other.
                    </P1>
                    <br />
                  </li>
                </StyledOrderedList>
              </li>
              <li>
                <P1>
                  <b>
                    Cancellation and Refunds: No Cancellation; No Refund; No
                    Return
                  </b>
                  . Except as specifically stated under these Notes Terms, once
                  you have completed the payment, whether Digital or Physical
                  Study Materials, you will neither be able to cancel your
                  purchase, nor will you be entitled to return the Unacademy
                  Notes or claim refunds for any reason whatsoever. Unacademy
                  reserves the right to provide refunds to you in certain
                  instances at its sole discretion.
                </P1>
                <br />
                <P1>
                  However, in case of Physical Study Material, you are entitled
                  to a replacement. To know the specific instances where
                  replacement is applicable, please refer to the{' '}
                  <b>‘Physical Study Material Terms and Conditions’</b> under
                  Part — I of these Notes Terms.
                </P1>
                <br />
              </li>
              <li>
                <P1>
                  All provisions contained herein are to be read in conjunction
                  with the Platform Terms, and any provision not specifically
                  included herein, including without limitation, provisions
                  pertaining to notices, force majeure, consumer grievances,
                  OFAC, indemnity and other miscellaneous provisions, shall
                  continue to apply to you in accordance with the Platform
                  Terms, unless otherwise specifically stated in these Notes
                  Terms. If you have any queries pertaining to the Platform
                  Terms including these Notes Terms, please write to{' '}
                  <a href="mailto:legal@unacademy.com">legal@unacademy.com</a>.
                </P1>
                <br />
              </li>
              <li>
                <P1>
                  <b>Disclaimers:</b> TO THE FULLEST EXTENT PERMITTED BY LAW,
                  UNACADEMY, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS
                  DISCLAIM AND EXCLUDE ALL WARRANTIES, EXPRESS OR IMPLIED, IN
                  CONNECTION WITH THE UNACADEMY NOTES OR ANY PART THEREOF, THE
                  CONTENT PROVIDED THEREIN AND YOUR USE THEREOF. TO THE FULLEST
                  EXTENT PERMITTED BY LAW, UNACADEMY EXCLUDES ALL WARRANTIES,
                  CONDITIONS, TERMS OR REPRESENTATIONS ABOUT THE ACCURACY,
                  SECURITY, RELIABILITY, QUALITY, AVAILABILITY OR COMPLETENESS
                  OF THE UNACADEMY NOTES OR ANY PART THEREOF AND ASSUMES NO
                  LIABILITY OR RESPONSIBILITY FOR ANY (I) ERRORS, MISTAKES, OR
                  INACCURACIES IN THE UNACADEMY NOTES OR ANY PART THEREOF; (II)
                  PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER,
                  RESULTING FROM YOUR PURCHASE OF UNACADEMY NOTES OR ANY PART
                  THEREOF; (III) ANY UNAUTHORIZED USE OF YOUR NAME, ADDRESS,
                  PAYMENT INFORMATION OR ANY OTHER INFORMATION BY ANY THIRD
                  PARTY DURING THE PURCHASE OF OR ACCESS TO THE UNACADEMY NOTES
                  OR ANY PART THEREOF ON THE UNACADEMY PLATFORM OR OTHERWISE;
                  AND (IV) COMPATIBILITY OF THE UNACADEMY NOTES OR ANY PART
                  THEREOF WITH YOUR DEVICE OR ANY LOSS, DAMAGE OR HARM CAUSED TO
                  YOU OR YOUR DEVICE DUE TO YOUR USE/ACCESS TO THE UNACADEMY
                  NOTES OR ANY PART THEREOF. UNACADEMY DOES NOT WARRANT,
                  ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANYTHING
                  CONTAINED IN THE UNACADEMY NOTES OR ANY PART THEREOF. AS WITH
                  THE PURCHASE OF A PRODUCT THROUGH ANY MEDIUM OR IN ANY
                  ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE
                  CAUTION WHERE APPROPRIATE.
                </P1>
                <br />
              </li>
              <li>
                <P1>
                  <b>Limitation of Liability:</b> NOTWITHSTANDING ANYTHING
                  CONTAINED HEREIN, UNACADEMY SHALL NOT BE LIABLE FOR ANY
                  INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, EXEMPLARY OR
                  CONSEQUENTIAL DAMAGES IN CONNECTION WITH OR ARISING OUT OF THE
                  SALE OF THE UNACADEMY NOTES OR ANY PART THEREOF TO YOU,
                  HOWEVER IT ARISES, WHETHER FOR BREACH OR IN TORT, INCLUDING
                  BUT NOT LIMITED TO LOSS OF PROFITS, DATA OR BUSINESS
                  INTERRUPTION, EVEN IF THAT PARTY HAS BEEN PREVIOUSLY ADVISED
                  OF THE POSSIBILITY OF SUCH DAMAGE. IN NO CIRCUMSTANCE SHALL
                  UNACADEMY BE LIABLE FOR ANY AMOUNT EXCEEDING THE PRICE PAID BY
                  YOU FOR THE UNACADEMY NOTES OR ANY PART THEREOF.
                </P1>
                <br />
                <P1>
                  YOU SPECIFICALLY ACKNOWLEDGE THAT UNACADEMY SHALL NOT BE
                  LIABLE FOR ANY CONTENT IN THE UNACADEMY NOTES THAT MAY BE
                  DEFAMATORY, OFFENSIVE, OR ILLEGAL.
                </P1>
                <br />
              </li>
              <li>
                <P1>
                  <b>Governing Law and Jurisdiction:</b> These Notes Terms shall
                  be governed by and construed in accordance with the laws of
                  and be subject to the jurisdiction stated in the Terms and
                  Conditions. Hence, please read the{' '}
                  <Link href="/terms#governing_laws">
                    ‘Applicable Law and Jurisdiction’
                  </Link>{' '}
                  section of the Terms and Conditions.
                </P1>
                <br />
              </li>
            </StyledOrderedList>
          </li>
        </StyledOrderedList>
        <br />
        <P1>
          <i>Last Updated on 24th June 2022</i>
        </P1>
      </RightContainer>
    </>
  );
};

Terms.pageInfo = {
  name: 'Unacademy Notes Terms',
  isMWebSupported: true,
  layoutOptions: {
    disableDarkMode: true
  }
};

export default Terms;
Terms.Layout = Layout;
Terms.Wrapper = PageReduxProvider;
